import { computed, ref } from "vue";
import { Tab, type LegendPayload } from "./edit-legend.models";
import { useTranslation } from "i18next-vue";

const forbiddenCharacters = ["*", "$", "!", "&", "%", "€", "£", "/", "?"];

const validPhotoMarkerShapes = [
  "circle",
  "thin_arrow",
  "large_arrow",
  "text_only",
];

const isTextValid = (str: string) => {
  const forbiddenPattern = new RegExp(
    `[${forbiddenCharacters.map((char) => `\\${char}`).join("")}]`
  );
  return !forbiddenPattern.test(str);
};

export const useLegendValidator = (legend: LegendPayload) => {
  const { t } = useTranslation();
  const showErrors = ref(false);

  const isLegendNameValid = computed(() => {
    return legend.name?.length > 0 && isTextValid(legend.name);
  });

  const areAllLayersValid = computed(() => {
    return legend.layer_configs.every((layer) => {
      return layer.name?.length > 0 && isTextValid(layer.name);
    });
  });

  const areAllScalesValid = computed(() => {
    return legend.scale_configs.every((scale) => {
      return scale.name?.length > 0 && isTextValid(scale.name);
    });
  });
  const duplicateNoteNames = computed(() => {
    const names = legend.note_configs.map((note) => note.name);
    return names.filter((name, index) => {
      return names.indexOf(name) !== index;
    });
  });

  const notesHaveLayer = computed(() => {
    const layerIds = legend.layer_configs.map((layer) => layer.id);
    // Only check non-photo notes for layer requirement
    return legend.note_configs
      .filter((note) => note.type !== "photo")
      .every(
        (note) =>
          !!note.props.layerId && layerIds.includes(note.props.layerId)
      );
  });

  const hasValidPhotoMarkers = computed(() => {
    const markers = legend.settings.favorite_photo_markers;
    // If no markers defined, consider it valid (will use defaults)
    if (!markers || !Array.isArray(markers)) return true;
    // If array exists, it must have at least one valid marker
    if (markers.length === 0) return false;
    return markers.every((marker) => validPhotoMarkerShapes.includes(marker));
  });

  const errors = computed<Record<Tab, string[]>>(() => {
    const errors: Record<Tab, string[]> = {
      [Tab.GENERAL]: [],
      [Tab.NOTES]: [],
      [Tab.LAYERS]: [],
      [Tab.SCALES]: [],
      [Tab.PHOTO_MARKERS]: [],
    };

    const forbidden = forbiddenCharacters.join("");
    if (!isLegendNameValid.value)
      errors[Tab.GENERAL].push(
        t("dashboard:alert.keyname.syntax", { forbidden })
      );

    if (!areAllLayersValid.value)
      errors[Tab.LAYERS].push(t("dashboard:alert.layer.syntax", { forbidden }));

    if (!areAllScalesValid.value)
      errors[Tab.SCALES].push(t("dashboard:alert.scale.syntax", { forbidden }));

    if (duplicateNoteNames.value.length > 0)
      errors[Tab.NOTES].push(
        `${t("dashboard:alert.note.unique.name")} (${[
          ...new Set(duplicateNoteNames.value),
        ].join(", ")})`
      );

    if (!notesHaveLayer.value)
      errors[Tab.NOTES].push(t("dashboard:alert.defect.must.have.layer"));

    if (!hasValidPhotoMarkers.value)
      errors[Tab.PHOTO_MARKERS].push(
        t("dashboard:alert.photo_marker.required")
      );

    return errors;
  });

  const isError = computed(() => {
    return Object.values(errors.value).some((error) => error.length > 0);
  });

  return {
    showErrors,
    isError,
    errors,
  };
};
